import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTypeTable,
  selectMaintenances,
} from "../../store/slices/maintenances";
import { selectCurrentUser } from "../../store/slices/currentUser";
import { selectBlocked, tryBlockUser } from "../../store/slices/users";
import {
  tryGetStudent,
  selectStudent,
  tryUpdateStudentData,
  tryUpdateAvatar,
} from "../../store/slices/students";
import {
  selectFileDocument,
  tryGetFileDocument,
} from "../../store/slices/backoffice-students";
import { NameTypography } from "../layout/name-display";
import { useDropzone } from "react-dropzone";
import {
  Divider,
  CardContent,
  Typography,
  Avatar,
  Button,
  Grid,
  TextField,
  Chip,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogActions,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CloudUploadOutlined,
  Cancel,
  Save,
  CameraAlt,
  Receipt,
  Badge,
  UploadFileOutlined,
} from "@mui/icons-material";
import { IsValidEmail } from "../../services/functions/validator";
import ModalMessages from "../layout/modal-messages";
import StudentSkeleton from "./skeleton";
import { NumericFormat, PatternFormat } from "react-number-format";
import convertToSelectList from "../layout/functions/convert-to-select-list";
import { useFormik } from "formik";
import Spinner from "../layout/spinner";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const validate = (values) => {
  const errors = {};

  const {
    email,
    fullAddress,
    cellPhone,
    telPhone,
    cellPhoneAreaCode,
    telPhoneAreaCode,
    emailCopy,
    unionCard,
    cardNumber,
    isFamiliar,
    conditionUTEDYC,
    birthDate,
    state,
    sectional,
  } = values;

  if (!email) errors.email = "Requerido";

  if (email !== emailCopy)
    errors.email = "Los correos electrónicos no coinciden";

  if (!IsValidEmail(email))
    errors.email = "Formato de correo electrónico invalido";

  if (!fullAddress) errors.fullAddress = "Requerido";

  if (!cellPhone) errors.cellPhone = "Requerido";

  if (cellPhone.length < 6)
    errors.cellPhone = "El número debe estar comprendido entre 6 y 10 caracteres";

  if (!telPhone) errors.telPhone = "Requerido";

  if (telPhone.length < 6)
    errors.telPhone = "El número debe estar comprendido entre 6 y 10 caracteres";

  if (!cellPhoneAreaCode) errors.cellPhoneAreaCode = "Requerido";

  if (!telPhoneAreaCode) errors.telPhoneAreaCode = "Requerido";

  if (isFamiliar && !!!unionCard) errors.unionCard = "Requerido";

  if (!conditionUTEDYC) errors.conditionUTEDYC = "Requerido";

  if (!birthDate || birthDate === "0001-01-01T00:00:00") errors.birthDate = "Requerido";

  if (dayjs(birthDate).$d.toString() === "Invalid Date") errors.birthDate = "Fecha inválida";

  if (!values.cuit || values.cuit === "") {
    errors.cuit = "Requerido";
  } else if (values.cuit.length < 11) {
    errors.cuit = "Longitud de CUIL inválida";
  }

  if (!state) errors.state = "Requerido";

  const conditionUTEDYCId = conditionUTEDYC === "Afiliado" ? 1
    : conditionUTEDYC === "Familiar Afiliado" ? 4
      : conditionUTEDYC === "Familiar Empleado" ? 5
        : conditionUTEDYC === "Empleado" ? 6
          : 0;

  if (conditionUTEDYCId === 4 && (!unionCard || unionCard === "" || unionCard === "S/I")) {
    errors.unionCard = "Requerido";
  }

  if ([1, 4].includes(conditionUTEDYCId) && (!cardNumber || cardNumber === "")) {
    errors.cardNumber = "Requerido";
  }

  if (!values.isTeacher && [1, 4, 5, 6].includes(conditionUTEDYCId) && (!sectional || sectional === 0)) {
    errors.sectional = "Requerido";
  }

  if ([4, 5].includes(conditionUTEDYCId) && (!values.relationAffiliate || values.relationAffiliate === 0)) {
    errors.relationAffiliate = "Requerido";
  }

  return errors;
};

const StudentData = ({ userId }) => {
  const { roleId } = useSelector(selectCurrentUser);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [file, setFile] = useState();
  const [preview, setPreview] = useState();
  const [enableEdit, setEnableEdit] = useState(false);
  const [enableEditProfilePicture, setEnableEditProfilePicture] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const fileDocument = useSelector(selectFileDocument);

  const dispatch = useDispatch();

  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const typeTables = useSelector(selectMaintenances);
  const user = useSelector(selectStudent);
  const blockedState = useSelector(selectBlocked);

  const { isLoading, errors } = user;

  const {
    cliSocialEconomics,
    cliNaturals,
    cliAddress,
    cliRelations,
    nationalities,
    students,
  } = user.clients;

  const fullName = `${cliNaturals.firstName} ${cliNaturals.lastName}`;

  const formik = useFormik({
    initialValues: {
      state: 0,
      type: "",
      username: "",
      emailCopy: "",
      email: "",
      firstName: "",
      lastName: "",
      cuit: "",
      cellPhoneAreaCode: 0,
      cellPhone: 0,
      telPhoneAreaCode: 0,
      telPhone: 0,
      gender: 0,
      nationality: 0,
      sectional: 0,
      conditionUTEDYC: 0,
      conditionWork: 0,
      relationAffiliate: 0,
      relationSectional: 0,
      academicLevel: 0,
      province: 0,
      fullAddress: "",
      notes: "",
      statusDoc: "",
      unionCard: "S/I",
      cardNumber: "",
      isFamiliar: "",
      birthDate: "",
      file: null,
      frontDocument: "",
      backDocument: "",
      isTeacher: false,
    },
    validate,
    onSubmit: async (values) => {
      let request = {};

      request.id = user.id;
      request.clientId = user.clientId;
      request.email = values.email;
      request.avatar = user.avatar ?? file;
      request.username = user.username;

      request.clients = {};
      request.clients.email = values.email;
      request.clients.statusId = values.state;

      request.clients.notes = values.notes;
      request.clients.nationality = values.nationality;
      request.clients.cliContacts = [];
      request.clients.cliDocuments = [];
      request.clients.cliRelations = [];

      const submitConditionUTEDYCId = values.conditionUTEDYC === "Afiliado" ? 1
        : values.conditionUTEDYC === "Familiar Afiliado" ? 4
          : values.conditionUTEDYC === "Familiar Empleado" ? 5
            : values.conditionUTEDYC === "Empleado" ? 6
              : 0;

      if (user.clients.cliDocuments) {
        user.clients.cliDocuments.forEach((doc) => {
          request.clients.cliDocuments.push({ ...doc });
        });
      }

      const cleanedCuil = (values.cuit || "").replace(/\D/g, "");
      const cuilIndex = request.clients.cliDocuments.findIndex((d) => d.typeDocumentId === 2);
      if (cuilIndex >= 0) {
        request.clients.cliDocuments[cuilIndex] = {
          ...request.clients.cliDocuments[cuilIndex],
          document: cleanedCuil,
        };
      } else if (cleanedCuil) {
        request.clients.cliDocuments.push({
          cliDocumentId: 0,
          clientId: user.clientId,
          document: cleanedCuil,
          typeDocumentId: 2,
          issuedDate: null,
          expiryDate: null,
        });
      }

      if ([1, 4].includes(submitConditionUTEDYCId) && values.cardNumber && values.cardNumber !== "") {
        const existingCardDocument = user.clients.cliDocuments?.find(doc => doc.typeDocumentId === 4);

        if (!existingCardDocument) {
          const newDocument = {
            cliDocumentId: 0,
            clientId: user.clientId,
            document: values.cardNumber,
            typeDocumentId: 4,
            issuedDate: null,
            expiryDate: null
          };
          request.clients.cliDocuments.push(newDocument);
        } else {
          const updatedDocument = {
            ...existingCardDocument,
            document: values.cardNumber
          };
          request.clients.cliDocuments.push(updatedDocument);
        }
      }

      cliRelations.forEach((x) => {
        let relation = { ...x };
        if (x.clientIdRel === -999996) {
          if (values.frontDocument) relation.additionalInfo = values.frontDocument;
        }
        if (x.clientIdRel === -999995) {
          if (values.backDocument) relation.additionalInfo = values.backDocument;
        }

        if (x.clientIdRel !== -999994 && x.clientIdRel !== -999997 && x.clientIdRel !== -999998 && x.clientIdRel !== -999999) {
          request.clients.cliRelations.push(relation);
        }
      });

      const needsAfiliado = submitConditionUTEDYCId === 1;
      const needsFamiliarAfiliado = submitConditionUTEDYCId === 4;
      const needsFamiliarEmpleado = values.conditionUTEDYC === "Familiar Empleado";
      const needsEmpleado = values.conditionUTEDYC === "Empleado";

      const existingClientIdRels = cliRelations.map(r => r.clientIdRel);

      if (needsAfiliado && !existingClientIdRels.includes(-999994)) {
        const newRelation = {
          cliRelationId: 0,
          clientId: user.clientId,
          clientIdRel: -999994,
          unionCard: "S/I",
          relSectional: !values.isTeacher ? (values.sectional || 0) : 0,
          relationshipId: 0,
          relationship: 0,
          additionalInfo: values.file || null
        };
        request.clients.cliRelations.push(newRelation);
      }

      if (needsFamiliarAfiliado && !existingClientIdRels.includes(-999999)) {
        const newRelation = {
          cliRelationId: 0,
          clientId: user.clientId,
          clientIdRel: -999999,
          unionCard: values.unionCard || "S/I",
          relSectional: !values.isTeacher ? (values.sectional || 0) : 0,
          relationshipId: values.relationAffiliate || 0,
          relationship: values.relationAffiliate || 0,
          additionalInfo: values.file || null
        };
        request.clients.cliRelations.push(newRelation);
      }

      if (needsFamiliarEmpleado && !existingClientIdRels.includes(-999998)) {
        const newRelation = {
          cliRelationId: 0,
          clientId: user.clientId,
          clientIdRel: -999998,
          unionCard: "S/I",
          relSectional: !values.isTeacher ? (values.sectional || 0) : 0,
          relationshipId: values.relationAffiliate || 0,
          relationship: values.relationAffiliate || 0,
          additionalInfo: values.file || null
        };
        request.clients.cliRelations.push(newRelation);
      }

      if (needsEmpleado && !existingClientIdRels.includes(-999997)) {
        const newRelation = {
          cliRelationId: 0,
          clientId: user.clientId,
          clientIdRel: -999997,
          unionCard: "S/I",
          relSectional: !values.isTeacher ? (values.sectional || 0) : 0,
          relationshipId: 0,
          additionalInfo: values.file || null
        };
        request.clients.cliRelations.push(newRelation);
      }

      cliRelations.forEach((x) => {
        if (
          x.clientIdRel === -999994 ||
          x.clientIdRel === -999997 ||
          x.clientIdRel === -999998 ||
          x.clientIdRel === -999999
        ) {
          let relation = { ...x };
          relation.relSectional = !values.isTeacher ? (values.sectional || 0) : 0;

          if (submitConditionUTEDYCId === 4) {
            relation.unionCard = values.unionCard || "S/I";
          } else {
            relation.unionCard = "S/I";
          }

          if (values.file) {
            relation.additionalInfo = values.file;
          }

          if ((submitConditionUTEDYCId === 4 || values.conditionUTEDYC === "Familiar Empleado") && values.relationAffiliate) {
            relation.relationshipId = values.relationAffiliate;
            relation.relationship = values.relationAffiliate;
          }

          request.clients.cliRelations.push(relation);
        }
      });

      request.clients.cliSocialEconomics = {};
      request.clients.cliNaturals = {
        ...cliNaturals,
        firstName: values.firstName,
        lastName: values.lastName,
      };
      request.clients.students = { ...students };
      request.clients.students.statusDoc = values.statusDoc;
      request.clients.cliAddress = cliAddress.map((x) => {
        let address = { ...x };
        if (x.typeAddress === 2) {
          address.fullAddress = values.fullAddress;
          address.province = values.province;
          address.phone = values.cellPhone;
          address.areaCode = values.cellPhoneAreaCode;
        }
        if (x.typeAddress === 1) {
          address.fullAddress = values.fullAddress;
          address.province = values.province;
          address.phone = values.telPhone;
          address.areaCode = values.telPhoneAreaCode;
        }
        return address;
      });

      request.clients.cliSocialEconomics.socialEconomicsId = cliSocialEconomics.socialEconomicsId;
      request.clients.cliSocialEconomics.sectional = null;
      request.clients.cliSocialEconomics.sectionalId = (!values.isTeacher && [1, 4, 5, 6].includes(submitConditionUTEDYCId)) ? values.sectional : null;
      request.clients.cliSocialEconomics.academicId = values.academicLevel;
      request.clients.cliSocialEconomics.birthDate = values.birthDate;
      request.clients.cliSocialEconomics.clientId = cliSocialEconomics.clientId;
      request.clients.cliSocialEconomics.genderId = values.gender;

      const selectedSituation = typeTables.TBLSITUATIONS?.data.find(
        (x) => x.description === values.conditionUTEDYC
      );

      request.clients.cliSocialEconomics.situationId = selectedSituation?.id || cliSocialEconomics.situationId;
      request.clients.cliSocialEconomics.labouralSituationId = values.conditionWork;

      const result = await dispatch(tryUpdateStudentData(request));

      if (result.type.includes('fulfilled')) {
        setJustSubmitted(true);
        setEnableEdit(false);

        setTimeout(async () => {
          await fetchData();
          setTimeout(() => {
            if (!enableEdit) {
              initializeForm();
            }
          }, 500);
        }, 1000);

        setTimeout(() => setJustSubmitted(false), 3000);
      }
    },
  });

  const handleCancel = () => {
    initializeForm();
    setEnableEdit(!enableEdit);
    setJustSubmitted(false);
    setFile();
    setPreview();
  };

  const initializeForm = () => {
    formik.setValues({
      state: user.clients.statuscli.id,
      type: user.clients.students.typeStudent.description,
      username: user.username,
      firstName: cliNaturals.firstName,
      lastName: cliNaturals.lastName,
      emailCopy: user.clients.email,
      email: user.clients.email,
      cuit: user.clients.cliDocuments?.find((x) => x.typeDocumentId === 2)?.document,
      cellPhoneAreaCode: cliAddress?.find((x) => x.tblTypeAddress.id === 2)?.areaCode ?? "",
      cellPhone: cliAddress?.find((x) => x.tblTypeAddress.id === 2)?.phone ?? "",
      telPhoneAreaCode: cliAddress?.find((x) => x.tblTypeAddress.id === 1)?.areaCode ?? "",
      telPhone: cliAddress?.find((x) => x.tblTypeAddress.id === 1)?.phone ?? "",
      gender: cliSocialEconomics.genderId,
      nationality: user.clients.nationality,
      sectional: (() => {
        // Lógica mejorada para leer la seccional (Safe Navigation)
        const situationId = cliSocialEconomics?.situation?.id;

        // Para todas las condiciones UTEDYC (1, 4, 5, 6), leer desde cliSocialEconomics.sectionalId primero
        if (situationId && [1, 4, 5, 6].includes(situationId)) {
          // Priorizar sectionalId del backend
          if (cliSocialEconomics?.sectionalId && cliSocialEconomics.sectionalId > 0) {
            return cliSocialEconomics.sectionalId;
          }

          // Si no hay sectionalId, buscar en las relaciones
          let relation = null;
          if (situationId === 1) {
            relation = cliRelations.find((x) => x.clientIdRel === -999994);
          } else if (situationId === 4) {
            relation = cliRelations.find((x) => x.clientIdRel === -999999);
          } else if (situationId === 5) {
            relation = cliRelations.find((x) => x.clientIdRel === -999998);
          } else if (situationId === 6) {
            relation = cliRelations.find((x) => x.clientIdRel === -999997);
          }

          if (relation?.relSectional && relation.relSectional > 0) {
            return relation.relSectional;
          }
          if (relation?.tblSectional?.description) {
            const sectional = typeTables.TBLSECTIONALS?.data.find(
              x => x.description === relation.tblSectional.description
            );
            return sectional?.id ?? 0;
          }
        }
        return user.sectional ?? 0;
      })(),
      conditionUTEDYC: cliSocialEconomics.situation.description,
      conditionWork: cliSocialEconomics.labouralSituationId,
      academicLevel: cliSocialEconomics.academicId,
      province: cliAddress[0]?.province,
      fullAddress: cliAddress[0]?.fullAddress,
      notes: user.clients.notes ? user.clients.notes : "",
      statusDoc: user.clients.students.statusDoc,
      unionCard: (() => {
        if (![4].includes(cliSocialEconomics.situation.id)) return "S/I";
        const unionCard = cliRelations.find((x) => x.clientIdRel === -999999)?.unionCard;
        return unionCard || "S/I";
      })(),
      cardNumber: (() => {
        if (![1, 4].includes(cliSocialEconomics.situation.id)) return "";
        const cardDocument = user.clients.cliDocuments?.find((x) => x.typeDocumentId === 4);
        return cardDocument?.document || "";
      })(),
      relationAffiliate: (() => {
        if ([4].includes(cliSocialEconomics.situation.id)) {
          const relation = cliRelations.find((x) => x.clientIdRel === -999999);
          if (relation?.relationshipId) return relation.relationshipId;
          if (relation?.relationship) return relation.relationship;
          if (relation?.tblRelationship?.description) {
            const relationship = typeTables.TBLRELATIONSHIPS?.data.find(
              x => x.description === relation.tblRelationship.description
            );
            return relationship?.id ?? 0;
          }
          return 0;
        }
        if ([5].includes(cliSocialEconomics.situation.id)) {
          const relation = cliRelations.find((x) => x.clientIdRel === -999998);
          if (relation?.relationshipId) return relation.relationshipId;
          if (relation?.tblRelationship?.description) {
            const relationship = typeTables.TBLRELATIONSHIPS?.data.find(
              x => x.description === relation.tblRelationship.description
            );
            return relationship?.id ?? 0;
          }
          return 0;
        }
        return 0;
      })(),
      isFamiliar: [1, 4].includes(cliSocialEconomics.situation.id) ? true : false,
      birthDate: cliSocialEconomics.birthDate,
      isTeacher: user.clients.teachers != null,
    });
  };

  const fetchData = async () => {
    await dispatch(tryGetStudent(userId));
  };

  useEffect(() => {
    dispatch(fetchTypeTable(49));
    dispatch(fetchTypeTable(45));
    dispatch(fetchTypeTable(17));
    dispatch(fetchTypeTable(19));
    dispatch(fetchTypeTable(16));
    dispatch(fetchTypeTable(20));
    dispatch(fetchTypeTable(14));
    dispatch(fetchTypeTable(18));
    dispatch(fetchTypeTable(51));
    dispatch(fetchTypeTable(23));
    dispatch(fetchTypeTable(62));
    fetchData();
  }, []);

  useEffect(() => {
    if (user.clientId !== 0 && !enableEdit && !justSubmitted) {
      if (cliSocialEconomics?.situation?.id && typeTables.TBLSECTIONALS?.data) {
        setTimeout(() => {
          if (!enableEdit && !justSubmitted) {
            initializeForm();
          }
        }, 100);
      }
    }
  }, [user, enableEdit, justSubmitted, typeTables.TBLSECTIONALS?.data]);

  useEffect(() => {
    if (!justSubmitted && !enableEdit && user.clientId !== 0) {
      initializeForm();
    }
  }, [justSubmitted]);

  useEffect(() => {
    if (user.clientId !== 0 && !enableEdit && !justSubmitted) {
      const currentSectional = formik.values.sectional;
      const expectedSectional = (() => {
        const situationId = cliSocialEconomics?.situation?.id;
        if (situationId && [1, 4, 5, 6].includes(situationId)) {
          if (cliSocialEconomics?.sectionalId && cliSocialEconomics.sectionalId > 0) {
            return cliSocialEconomics.sectionalId;
          }
          let relation = null;
          if (situationId === 1) relation = cliRelations.find((x) => x.clientIdRel === -999994);
          else if (situationId === 4) relation = cliRelations.find((x) => x.clientIdRel === -999999);
          else if (situationId === 5) relation = cliRelations.find((x) => x.clientIdRel === -999998);
          else if (situationId === 6) relation = cliRelations.find((x) => x.clientIdRel === -999997);

          if (relation?.relSectional && relation.relSectional > 0) return relation.relSectional;
        }
        return 0;
      })();

      if (currentSectional !== expectedSectional) {
        initializeForm();
      }
    }
  }, [user.clients.cliSocialEconomics?.sectionalId, user.clients.cliRelations]);

  useEffect(() => {
    if (user.clientId !== 0 && !enableEdit && !justSubmitted && typeTables.TBLSECTIONALS?.data) {
      setTimeout(() => {
        if (!enableEdit && !justSubmitted) {
          initializeForm();
        }
      }, 200);
    }
  }, [typeTables.TBLSECTIONALS?.data, user.clientId, enableEdit, justSubmitted]);

  useEffect(() => {
    if (user.clientId !== 0 && !enableEdit && !justSubmitted &&
      typeTables.TBLSECTIONALS?.data &&
      cliSocialEconomics?.situation?.id) {

      const currentSectional = formik.values.sectional;
      const expectedSectional = (() => {
        const situationId = cliSocialEconomics?.situation?.id;
        if (situationId && [1, 4, 5, 6].includes(situationId)) {
          if (cliSocialEconomics?.sectionalId && cliSocialEconomics.sectionalId > 0) {
            return cliSocialEconomics.sectionalId;
          }
          let relation = null;
          if (situationId === 1) relation = cliRelations.find((x) => x.clientIdRel === -999994);
          else if (situationId === 4) relation = cliRelations.find((x) => x.clientIdRel === -999999);
          else if (situationId === 5) relation = cliRelations.find((x) => x.clientIdRel === -999998);
          else if (situationId === 6) relation = cliRelations.find((x) => x.clientIdRel === -999997);

          if (relation?.relSectional && relation.relSectional > 0) return relation.relSectional;
        }
        return 0;
      })();

      if (currentSectional !== expectedSectional && expectedSectional > 0) {
        setTimeout(() => {
          if (!enableEdit && !justSubmitted) {
            initializeForm();
          }
        }, 100);
      }
    }
  }, [user.clientId, enableEdit, justSubmitted, typeTables.TBLSECTIONALS?.data, cliSocialEconomics?.situation?.id, cliRelations]);

  const getFileDocument = async () => {
    const action = await dispatch(tryGetFileDocument(user.clientId));
    if (action.payload.data.isValid) {
      action.payload.data.data.map((x) => {
        let pdfWindow = window.open("");
        pdfWindow.document.write(
          "<iframe width='100%' height='100%' src='" + x.file + "'></iframe>"
        );
      });
    }
  };

  const blockUser = async (userId) => {
    const action = await dispatch(tryBlockUser(userId));
    if (action.payload.isValid) setOpenDialog(true);
  };

  const thumbsContainer = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 16,
  };

  const thumb = {
    display: "inline-flex",
    borderRadius: 2,
    border: "1px solid #eaeaea",
    marginBottom: 8,
    marginRight: 8,
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: "border-box",
  };

  const thumbInner = {
    display: "flex",
    minWidth: 0,
    overflow: "hidden",
  };

  const img = {
    display: "block",
    width: "auto",
    height: "100%",
  };

  const { getRootProps, getInputProps } = useDropzone({
    noKeyboard: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
    },
    maxFiles: 1,
    multiple: false,
    maxSize: 5000000,
    onDropAccepted: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64data = reader.result;
        setFile(base64data);
        setPreview(URL.createObjectURL(file));
      };
    },
  });

  const useCustomDropzone = (fieldName, onDropAccepted) => {
    const {
      getRootProps,
      getInputProps,
      acceptedFiles,
    } = useDropzone({
      noKeyboard: true,
      accept: {
        "image/png": [".png"],
        "image/jpeg": [".jpeg", ".jpg"],
        "application/pdf": [".pdf"],
      },
      maxFiles: 1,
      multiple: false,
      maxSize: 10000000,
      onDropAccepted: onDropAccepted,
    });

    const files = acceptedFiles.map((file) => (
      <ListItem key={file.path}>
        <ListItemIcon>
          <UploadFileOutlined color="primary" />
        </ListItemIcon>
        <ListItemText primary={file.path} />
      </ListItem>
    ));

    return { getRootProps, getInputProps, files };
  };

  const { getRootProps: getRootProps1, getInputProps: getInputProps1, files: files1 } = useCustomDropzone("file", (acceptedFiles1) => {
    const file = acceptedFiles1[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64data = reader.result;
      formik.setFieldValue("file", base64data);
    };
  });

  const { getRootProps: getRootProps2, getInputProps: getInputProps2, files: files2 } = useCustomDropzone("frontDocument", (acceptedFiles2) => {
    const file2 = acceptedFiles2[0];
    const reader = new FileReader();
    reader.readAsDataURL(file2);
    reader.onloadend = () => {
      const base64data = reader.result;
      formik.setFieldValue("frontDocument", base64data);
    };
  });

  const { getRootProps: getRootProps3, getInputProps: getInputProps3, files: files3 } = useCustomDropzone("backDocument", (acceptedFiles3) => {
    const file3 = acceptedFiles3[0];
    const reader = new FileReader();
    reader.readAsDataURL(file3);
    reader.onloadend = () => {
      const base64data = reader.result;
      formik.setFieldValue("backDocument", base64data);
    };
  });

  const thumbs = preview && (
    <div style={thumb}>
      <div style={thumbInner}>
        <img
          src={preview}
          style={img}
          alt="Imagen de perfil"
          onLoad={() => {
            URL.revokeObjectURL(preview);
          }}
        />
      </div>
    </div>
  );

  const affiliateNumber = user.clients.cliDocuments?.filter((x) => x.typeDocumentId == 4) || false;

  const handleEditDetail = () => {
    if (enableEdit) {
      formik.handleSubmit();
    } else {
      setJustSubmitted(false);
      setEnableEdit(true);
    }
  };

  const handleEditProfilePicture = () => {
    enableEditProfilePicture && dispatch(tryUpdateAvatar({ userId: user.id, image: file }));
    setEnableEditProfilePicture(!enableEditProfilePicture);
  };

  const handleCancelEditProfile = () => {
    setEnableEditProfilePicture(!enableEditProfilePicture);
    setFile();
    setPreview();
  };

  const getStatusDoc = () => {
    const status = typeTables.TBLSTATUSADDITIONALDOCS.data.find((x) => x.id == user.clients.students.statusDoc);
    if (status) return status.description;
    if (!status) return "";
  };

  const getColorStatusDoc = () => {
    if (user.clients.students.statusDoc == 1 || user.clients.students.statusDoc == 3) return "success";
    if (user.clients.students.statusDoc == 2) return "error";
    return "primary";
  };

  // -------------------------------------------------------------
  // CONTENIDO DEL FORMULARIO CENTRALIZADO
  // -------------------------------------------------------------
  const renderFormContent = () => (
    <form onSubmit={formik.handleSubmit} style={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      <Box sx={{ overflowX: "hidden" }}>
        <Grid container pt={2} textAlign="left" spacing={1} rowSpacing={2} direction="row">

          {enableEdit && (
            <>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>Nombre: *</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField required fullWidth id="firstName" size="small" onChange={formik.handleChange} value={formik.values.firstName} error={formik.touched.firstName && Boolean(formik.errors.firstName)} helperText={formik.touched.firstName && formik.errors.firstName} autoComplete="off" />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>Apellido: *</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField required fullWidth id="lastName" size="small" onChange={formik.handleChange} value={formik.values.lastName} error={formik.touched.lastName && Boolean(formik.errors.lastName)} helperText={formik.touched.lastName && formik.errors.lastName} autoComplete="off" />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Estado:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="state" id="state" value={formik.values.state} size="small" onChange={formik.handleChange} disabled={!enableEdit} error={formik.touched.state && Boolean(formik.errors.state)}>
                  {typeTables.TBLSTATUSCLIS ? convertToSelectList(typeTables.TBLSTATUSCLIS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>)) : []}
                </Select>
                {formik.touched.state && <FormHelperText error={true}>{formik.errors.state}</FormHelperText>}
              </FormControl>
            ) : (
              <Chip size="small" label={typeTables.TBLSTATUSCLIS.data.map((x) => { if (x.id == formik.values.state) return x.description; })} color={formik.values.state == 1 ? "success" : "error"} />
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Tipo de Alumno:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Chip size="small" label={formik.values.type} color="success" />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Fecha de Nacimiento:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DesktopDatePicker id="birthDate" disableFuture name="birthDate" label="Fecha de Nacimiento" inputFormat="DD/MM/YYYY" value={formik.values.birthDate} onChange={(value) => { formik.setFieldValue("birthDate", dayjs(value).format("YYYY-MM-DD")); }} type="date" disabled={!enableEdit} renderInput={(params) => (<TextField {...params} size="small" fullWidth variant="outlined" error={formik.touched.birthDate && Boolean(formik.errors.birthDate)} helperText={formik.touched.birthDate && formik.errors.birthDate} />)} />
              </LocalizationProvider>
            ) : (
              <Typography>{formik.values.birthDate ? dayjs(formik.values.birthDate).format("DD/MM/YYYY") : "No especificada"}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Cond. frente UTEDYC:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="conditionUTEDYC" id="conditionUTEDYC" value={formik.values.conditionUTEDYC} size="small" onChange={formik.handleChange} disabled={!enableEdit} error={formik.touched.conditionUTEDYC && Boolean(formik.errors.conditionUTEDYC)}>
                  {typeTables.TBLSITUATIONS ? convertToSelectList(typeTables.TBLSITUATIONS.data).map((x) => (<MenuItem key={x.value} value={x.text}>{x.text}</MenuItem>)) : []}
                </Select>
                {formik.touched.conditionUTEDYC && <FormHelperText error={true}>{formik.errors.conditionUTEDYC}</FormHelperText>}
              </FormControl>
            ) : (
              <Typography>{formik.values.conditionUTEDYC}</Typography>
            )}
          </Grid>

          {(() => {
            const conditionId = formik.values.conditionUTEDYC === "Afiliado" ? 1 : formik.values.conditionUTEDYC === "Familiar Afiliado" ? 4 : formik.values.conditionUTEDYC === "Familiar Empleado" ? 5 : formik.values.conditionUTEDYC === "Empleado" ? 6 : 0;
            if (![1, 4].includes(conditionId)) return null;

            return (
              <>
                {conditionId === 4 && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography fontWeight={700}>Nro carnet del familiar afiliado:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {enableEdit ? (
                        <TextField required fullWidth id="unionCard" size="small" onChange={formik.handleChange} value={formik.values.unionCard} disabled={!enableEdit} error={formik.touched.unionCard && Boolean(formik.errors.unionCard)} helperText={formik.touched.unionCard && formik.errors.unionCard} autoComplete="off" />
                      ) : (
                        <Typography>{formik.values.unionCard}</Typography>
                      )}
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={4}>
                  <Typography fontWeight={700}>{conditionId === 1 ? "Nro de afiliado:" : "Nro de carnet:"}</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  {enableEdit ? (
                    <TextField required fullWidth id="cardNumber" size="small" name="cardNumber" onChange={formik.handleChange} value={formik.values.cardNumber} disabled={!enableEdit} error={formik.touched.cardNumber && Boolean(formik.errors.cardNumber)} helperText={formik.touched.cardNumber && formik.errors.cardNumber} autoComplete="off" />
                  ) : (
                    <Typography>{formik.values.cardNumber}</Typography>
                  )}
                </Grid>
              </>
            );
          })()}

          {(() => {
            const conditionId = formik.values.conditionUTEDYC === "Afiliado" ? 1 : formik.values.conditionUTEDYC === "Familiar Afiliado" ? 4 : formik.values.conditionUTEDYC === "Familiar Empleado" ? 5 : formik.values.conditionUTEDYC === "Empleado" ? 6 : 0;
            if (![4, 5].includes(conditionId)) return null;

            return (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography fontWeight={700}>Relación con el afiliado:</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  {enableEdit ? (
                    <FormControl fullWidth>
                      <Select name="relationAffiliate" id="relationAffiliate" value={formik.values.relationAffiliate} size="small" onChange={formik.handleChange} disabled={!enableEdit} error={formik.touched.relationAffiliate && Boolean(formik.errors.relationAffiliate)}>
                        {typeTables.TBLRELATIONSHIPS && convertToSelectList(typeTables.TBLRELATIONSHIPS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                      </Select>
                      {formik.touched.relationAffiliate && <FormHelperText error={true}>{formik.errors.relationAffiliate}</FormHelperText>}
                    </FormControl>
                  ) : (
                    <Typography>
                      {[4].includes(cliSocialEconomics.situation.id) && cliRelations.find((x) => x.clientIdRel === -999999)?.tblRelationship?.description}
                      {[5].includes(cliSocialEconomics.situation.id) && cliRelations.find((x) => x.clientIdRel === -999998)?.tblRelationship?.description}
                    </Typography>
                  )}
                </Grid>
              </>
            );
          })()}

          {(() => {
            const conditionId = formik.values.conditionUTEDYC === "Afiliado" ? 1 : formik.values.conditionUTEDYC === "Familiar Afiliado" ? 4 : formik.values.conditionUTEDYC === "Familiar Empleado" ? 5 : formik.values.conditionUTEDYC === "Empleado" ? 6 : 0;
            if (![1, 4, 5, 6].includes(conditionId) || formik.values.isTeacher) return null;

            return (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography fontWeight={700}>Seccional:</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  {enableEdit ? (
                    <FormControl fullWidth>
                      <Select name="sectional" id="sectional" value={formik.values.sectional} size="small" onChange={formik.handleChange} disabled={!enableEdit} error={formik.touched.sectional && Boolean(formik.errors.sectional)}>
                        {typeTables.TBLSECTIONALS && convertToSelectList(typeTables.TBLSECTIONALS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                      </Select>
                      {formik.touched.sectional && <FormHelperText error={true}>{formik.errors.sectional}</FormHelperText>}
                    </FormControl>
                  ) : (
                    <Typography>
                      {(() => {
                        const situationId = cliSocialEconomics?.situation?.id;

                        if (situationId && [1, 4, 5, 6].includes(situationId)) {
                          if (cliSocialEconomics?.sectionalId && cliSocialEconomics.sectionalId > 0) {
                            const sectional = typeTables.TBLSECTIONALS?.data.find(x => x.id === cliSocialEconomics.sectionalId);
                            return sectional?.description || "No asignado";
                          }

                          let relation = null;
                          if (situationId === 1) relation = cliRelations.find((x) => x.clientIdRel === -999994);
                          else if (situationId === 4) relation = cliRelations.find((x) => x.clientIdRel === -999999);
                          else if (situationId === 5) relation = cliRelations.find((x) => x.clientIdRel === -999998);
                          else if (situationId === 6) relation = cliRelations.find((x) => x.clientIdRel === -999997);

                          if (relation?.tblSectional?.description) return relation.tblSectional.description;
                          if (relation?.relSectional && relation.relSectional > 0) {
                            const sectional = typeTables.TBLSECTIONALS?.data.find(x => x.id === relation.relSectional);
                            return sectional?.description || "No asignado";
                          }
                          return "No asignado";
                        }
                        return "No aplica";
                      })()}
                    </Typography>
                  )}
                </Grid>
              </>
            );
          })()}

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Nacionalidad:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="nationality" id="nationality" value={formik.values.nationality} size="small" onChange={formik.handleChange} disabled={!enableEdit} error={formik.touched.nationality && Boolean(formik.errors.nationality)}>
                  {typeTables.TBLNATIONALITYS ? convertToSelectList(typeTables.TBLNATIONALITYS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>)) : null}
                </Select>
              </FormControl>
            ) : (
              <Typography>{nationalities.description}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>CUIL:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <PatternFormat id="cuit" name="cuit" label="CUIL" customInput={TextField} format="##-########-#" mask="_" size="small" variant="outlined" required value={formik.values.cuit} onChange={(e) => { const cleaned = e.target.value.replace(/-/g, "").replace(/_/g, ""); formik.setFieldValue("cuit", cleaned); }} error={formik.touched.cuit && Boolean(formik.errors.cuit)} helperText={formik.touched.cuit && formik.errors.cuit} />
              </FormControl>
            ) : (
              <Typography><PatternFormat id="cuit" customInput={TextField} format="##-########-#" mask="_" size="small" variant="standard" disabled value={formik.values.cuit} /></Typography>
            )}
          </Grid>

          {affiliateNumber.length !== 0 && (
            <>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>Nro. de afiliado:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography>{affiliateNumber[0].document}</Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Email: *</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <TextField required fullWidth id="email" size="small" onChange={formik.handleChange} type="email" value={formik.values.email} onPaste={(event) => event.preventDefault()} disabled={!enableEdit} error={formik.touched.email && Boolean(formik.errors.email)} helperText={formik.touched.email && formik.errors.email} autoComplete="off" />
            ) : (
              <Typography sx={{ wordBreak: "break-all" }}>{formik.values.email}</Typography>
            )}
          </Grid>

          {enableEdit && (
            <>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={700}>Email Confirmación: *</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField required fullWidth id="emailCopy" type="email" size="small" onPaste={(event) => event.preventDefault()} onChange={formik.handleChange} value={formik.values.emailCopy} disabled={!enableEdit} autoComplete="off" />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Dirección: *</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <TextField id="fullAddress" variant="outlined" size="small" fullWidth onChange={formik.handleChange} value={formik.values.fullAddress} error={formik.touched.fullAddress && Boolean(formik.errors.fullAddress)} helperText={formik.touched.fullAddress && formik.errors.fullAddress} autoComplete="off" />
            ) : (
              <Typography>{formik.values.fullAddress}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Provincia:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="province" id="province" value={formik.values.province} size="small" disabled={!enableEdit} onChange={formik.handleChange} error={formik.touched.province && Boolean(formik.errors.province)}>
                  {typeTables.TBLPROVINCES && convertToSelectList(typeTables.TBLPROVINCES.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{cliAddress[0]?.tblProvinces?.description || "N/A"}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Celular: *</Typography>
          </Grid>
          <Grid item xs={4} sm={3}>
            {enableEdit ? (
              <FormControl fullWidth>
                <TextField name="cellPhoneAreaCode" label="Cód. Área" variant="outlined" size="small" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxlength: "4" }} onChange={(e) => { const { value } = e.target; if (value.length > 0 && value[0] === "0") { formik.setFieldValue("cellPhoneAreaCode", value.substring(1)); } else { formik.setFieldValue("cellPhoneAreaCode", value); } }} value={formik.values.cellPhoneAreaCode} error={formik.touched.cellPhoneAreaCode && Boolean(formik.errors.cellPhoneAreaCode)} helperText={formik.touched.cellPhoneAreaCode && formik.errors.cellPhoneAreaCode} autoComplete="off" />
              </FormControl>
            ) : (
              <Typography>{formik.values.cellPhoneAreaCode}</Typography>
            )}
          </Grid>
          <Grid item xs={8} sm={5}>
            {enableEdit ? (
              <FormControl fullWidth>
                <NumericFormat id="cellPhone" label="Número" variant="outlined" size="small" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxlength: "10" }} customInput={TextField} onChange={formik.handleChange} value={formik.values.cellPhone} error={formik.touched.cellPhone && Boolean(formik.errors.cellPhone)} helperText={formik.touched.cellPhone && formik.errors.cellPhone} autoComplete="off" />
              </FormControl>
            ) : (
              <Typography>{formik.values.cellPhone}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Teléfono: *</Typography>
          </Grid>
          <Grid item xs={4} sm={3}>
            {enableEdit ? (
              <FormControl fullWidth>
                <TextField id="telPhoneAreaCode" label="Cód. Área" variant="outlined" size="small" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxlength: "4" }} onChange={(e) => { const { value } = e.target; if (value.length > 0 && value[0] === "0") { formik.setFieldValue("telPhoneAreaCode", value.substring(1)); } else { formik.setFieldValue("telPhoneAreaCode", value); } }} value={formik.values.telPhoneAreaCode} error={formik.touched.telPhoneAreaCode && Boolean(formik.errors.telPhoneAreaCode)} helperText={formik.touched.telPhoneAreaCode && formik.errors.telPhoneAreaCode} autoComplete="off" />
              </FormControl>
            ) : (
              <Typography>{formik.values.telPhoneAreaCode}</Typography>
            )}
          </Grid>
          <Grid item xs={8} sm={5}>
            {enableEdit ? (
              <FormControl fullWidth>
                <NumericFormat id="telPhone" label="Número" variant="outlined" size="small" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxlength: "10" }} fullWidth customInput={TextField} onChange={formik.handleChange} value={formik.values.telPhone} error={formik.touched.telPhone && Boolean(formik.errors.telPhone)} helperText={formik.touched.telPhone && formik.errors.telPhone} autoComplete="off" />
              </FormControl>
            ) : (
              <Typography>{formik.values.telPhone}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Género:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="gender" id="gender" value={formik.values.gender} disabled={!enableEdit} size="small" onChange={formik.handleChange}>
                  {typeTables.TBLGENDER && convertToSelectList(typeTables.TBLGENDER.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{cliSocialEconomics.gender.description}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Cond. Laboral:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="conditionWork" id="conditionWork" value={formik.values.conditionWork} disabled={!enableEdit} size="small" onChange={formik.handleChange}>
                  {typeTables.TBLLABOURSITUATIONS && convertToSelectList(typeTables.TBLLABOURSITUATIONS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{cliSocialEconomics.labouralSituation.description}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Nivel Académico:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <FormControl fullWidth>
                <Select name="academicLevel" id="academicLevel" value={formik.values.academicLevel} disabled={!enableEdit} size="small" onChange={formik.handleChange}>
                  {typeTables.TBLACADEMICS && convertToSelectList(typeTables.TBLACADEMICS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{cliSocialEconomics.academic.description}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Información de emergencia:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {enableEdit ? (
              <TextField id="notes" multiline rows={5} fullWidth onChange={formik.handleChange} value={formik.values.notes} />
            ) : (
              <Typography>{user.clients.notes}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography fontWeight={700}>Doc. Adicional:</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            {roleId == 1 && enableEdit ? (
              <FormControl fullWidth>
                <Select name="statusDoc" id="statusDoc" value={formik.values.statusDoc} disabled={!enableEdit} size="small" onChange={formik.handleChange}>
                  {typeTables.TBLSTATUSADDITIONALDOCS && convertToSelectList(typeTables.TBLSTATUSADDITIONALDOCS.data).map((x) => (<MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>))}
                </Select>
              </FormControl>
            ) : (
              user.clients.students.statusDoc && (
                <Chip size="small" label={getStatusDoc()} color={getColorStatusDoc()} />
              )
            )}
          </Grid>

          {/* DOCUMENTOS Y DROPZONES */}
          {roleId === 1 && enableEdit && (
            <Grid container spacing={2} sx={{ mt: 2, px: 1 }}>
              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ borderStyle: "dashed", borderColor: "#85BFE6", height: "100%", overflow: "auto", p: 1 }}>
                  <section>
                    <div {...getRootProps2({ className: "dropzone" })}>
                      <input {...getInputProps2()} />
                      <Box textAlign="center" pt={1}>
                        <Typography color="primary" variant="h5" fontWeight={700} pb={1}>FOTO DNI FRENTE</Typography>
                        <Typography color="primary" variant="subtitle2" fontWeight={700} pb={1}>Seleccione un archivo o arrástrelo aquí</Typography>
                        <CloudUploadOutlined color="primary" fontSize="large" />
                        <Typography color="primary" variant="subtitle2" pt={1}>JPG, PNG o PDF max 10 Mb.</Typography>
                      </Box>
                    </div>
                    <List sx={{ width: "100%" }}>{formik.values.frontDocument && files2}</List>
                  </section>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ borderStyle: "dashed", borderColor: "#85BFE6", height: "100%", overflow: "auto", p: 1 }}>
                  <section>
                    <div {...getRootProps3({ className: "dropzone" })}>
                      <input {...getInputProps3()} />
                      <Box textAlign="center" pt={1}>
                        <Typography color="primary" variant="h5" fontWeight={700} pb={1}>FOTO DNI DORSO</Typography>
                        <Typography color="primary" variant="subtitle2" fontWeight={700} pb={1}>Seleccione un archivo o arrástrelo aquí</Typography>
                        <CloudUploadOutlined color="primary" fontSize="large" />
                        <Typography color="primary" variant="subtitle2" pt={1}>JPG, PNG o PDF max 10 Mb.</Typography>
                      </Box>
                    </div>
                    <List sx={{ width: "100%" }}>{formik.values.backDocument && files3}</List>
                  </section>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                {[4, 5, 6].includes(cliSocialEconomics?.situationId) ? (
                  <Paper elevation={3} sx={{ borderStyle: "dashed", borderColor: "#85BFE6", height: "100%", overflow: "auto", p: 1 }}>
                    <section>
                      <div {...getRootProps1({ className: "dropzone" })}>
                        <input {...getInputProps1()} />
                        <Box textAlign="center" pt={1}>
                          <Typography color="primary" variant="h5" fontWeight={700} pb={1}>RECIBO DE SUELDO</Typography>
                          <Typography color="primary" variant="subtitle2" fontWeight={700} pb={1}>Seleccione un archivo o arrástrelo aquí</Typography>
                          <CloudUploadOutlined color="primary" fontSize="large" />
                          <Typography color="primary" variant="subtitle2" pt={1}>JPG, PNG o PDF max 10 Mb.</Typography>
                        </Box>
                      </div>
                      <List sx={{ width: "100%" }}>{formik.values.file && files1}</List>
                    </section>
                  </Paper>
                ) : (
                  <Box textAlign="center" pt={6}>
                    <img src={process.env.PUBLIC_URL + "/images/upload-files.png"} width="100px" alt="upload" />
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {roleId === 1 && (
            <Grid item xs={12} pt={3} align="center">
              <Button onClick={() => getFileDocument()} sx={{ height: "35px" }} variant="text" color="primary">
                {"<< Descargar documentos del alumno >>"}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid textAlign="center" container pt={2} spacing={2} justifyContent="center">
              <Grid item xs={12} sm="auto">
                <Button disabled={enableEdit && !formik.dirty} onClick={() => handleEditDetail()} sx={{ width: { xs: "100%", sm: "170px" }, height: "35px" }} variant="contained">
                  {enableEdit ? "GUARDAR" : "EDITAR"}
                </Button>
              </Grid>
              {enableEdit && (
                <Grid item xs={12} sm="auto">
                  <Button onClick={() => handleCancel()} sx={{ width: { xs: "100%", sm: "170px" }, height: "35px" }} variant="outlined" color="error">
                    Cancelar
                  </Button>
                </Grid>
              )}
              {!enableEdit && roleId == 1 && formik.values.state !== 3 && (
                <Grid item xs={12} sm="auto">
                  <Button onClick={() => setConfirmationDialog(true)} sx={{ width: { xs: "100%", sm: "170px" }, height: "35px" }} disabled={blockedState.isLoading} variant="contained" color={formik.values.state == 1 ? "error" : "success"}>
                    {blockedState.isLoading && <Spinner color="primary" />}
                    {formik.values.state == 1 ? "BLOQUEAR" : "HABILITAR"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </form>
  );

  return (
    <>
      {!isLoading && formik.values.state !== 0 ? (
        <>
          {user.clientId !== 0 && (
            <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  pt: 3,
                  pb: 2,
                  px: 2,
                  boxSizing: "border-box",
                }}
              >
                {enableEditProfilePicture ? (
                  <Box sx={{ border: "2px #1d4d72 solid", borderRadius: 2 }}>
                    <section className="container">
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Grid container sx={{ ":hover": { cursor: "pointer" } }} columns={{ xs: 12, sm: 8, md: 4 }}>
                          <Grid item xs={2} sm={4} md={4} container direction="row" justifyContent="center" alignItems="center">
                            <CloudUploadOutlined color="primary" fontSize="large" />
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <Typography color="primary" align="center" variant="subtitle2" sx={{ fontWeight: 700, paddingTop: 5 }}>Seleccione un archivo o arrástrelo aquí</Typography>
                            <Typography color="primary" align="center" variant="subtitle2" sx={{ paddingTop: 5, paddingBottom: 2 }}>Imagen con un tamaño no mayor a 5 MB.</Typography>
                          </Grid>
                        </Grid>
                      </div>
                      <aside style={thumbsContainer}>{thumbs}</aside>
                    </section>
                  </Box>
                ) : (
                  <Avatar sx={{ width: 135, height: 135, margin: "25px auto", boxShadow: 1, textTransform: "uppercase" }} alt={`Foto de Perfil de ${cliNaturals.firstName} ${cliNaturals.lastName}`} src={user.avatar}>
                    {`${cliNaturals.firstName} ${cliNaturals.lastName}`}
                  </Avatar>
                )}

                <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "center" }}>
                  <IconButton onClick={() => handleEditProfilePicture()} sx={{ width: "170px", height: "35px" }} variant="contained" color="primary" disabled={enableEditProfilePicture && !preview} disableRipple>
                    {enableEditProfilePicture ? <Save /> : <CameraAlt />}
                  </IconButton>
                  {enableEditProfilePicture && (
                    <IconButton onClick={() => handleCancelEditProfile()} sx={{ width: "170px", height: "35px" }} variant="outlined" color="error" disableRipple>
                      <Cancel />
                    </IconButton>
                  )}
                </Box>

                <NameTypography
                  sx={{
                    fontSize: { xs: 22, sm: 25 },
                    fontWeight: "bold",
                    textAlign: "center",
                    width: "100%",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  <strong style={{ fontWeight: "bold" }}>{fullName}</strong>
                </NameTypography>
              </Box>

              {isMobile ? (
                <Accordion defaultExpanded={false} sx={{ boxShadow: "none", borderTop: "1px solid #eee", width: "100%" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={700} color="primary">
                      Ver Detalles del Alumno
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ overflowX: "hidden", p: { xs: 1, sm: 2 } }}>
                    {renderFormContent()}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <CardContent sx={{ overflowX: "hidden" }}>
                  <Divider light sx={{ borderColor: "secondary.light" }} />
                  {renderFormContent()}
                </CardContent>
              )}
            </Box>
          )}

          {openDialog && (
            <Dialog open={openDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
              <DialogTitle id="alert-dialog-title">{"La información ha sido guardada con éxito"}</DialogTitle>
              <DialogActions><Button onClick={() => { setOpenDialog(false); fetchData(); }} autoFocus>Aceptar</Button></DialogActions>
            </Dialog>
          )}

          {confirmationDialog && (
            <Dialog open={confirmationDialog} onClose={confirmationDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
              <DialogTitle id="alert-dialog-title">{formik.values.state == 1 ? "¿Estás seguro que deseas bloquear al usuario?" : "¿Estás seguro que deseas habilitar al usuario?"}</DialogTitle>
              <DialogActions>
                <Button onClick={() => { blockUser(userId); setConfirmationDialog(false); }} autoFocus>Aceptar</Button>
                <Button onClick={() => setConfirmationDialog(false)} autoFocus>Cancelar</Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      ) : (
        <StudentSkeleton />
      )}
      {errors && errors.length > 0 && <ModalMessages errors={errors} />}
      {fileDocument.errors && fileDocument.errors.length > 0 && <ModalMessages errors={fileDocument.errors} />}
    </>
  );
};

export default StudentData;